import React, { createContext, useMemo, useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../authentication';

import {
  useGetOrganizationsQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation
} from '../hooks';
import { useNotifications } from '../../../providers/NotificationsProvider';

const CURRENT_ORGANIZATION_KEY = 'org_id';

const OrganizationsContext = createContext();

const OrganizationsProvider = ({ children = null }) => {
  const { accessToken } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const organizationQuery = useGetOrganizationsQuery(accessToken);
  const createOrganizationMutation = useCreateOrganizationMutation(accessToken);
  const updateOrganizationMutation = useUpdateOrganizationMutation(accessToken);
  const deleteOrganizationMutation = useDeleteOrganizationMutation(accessToken);

  const queryKey = ['membership', 'organizations'];

  const selectOrganization = useCallback(
    (id) => {
      const organization = organizationQuery?.data?.find((org) => org.id === id);

      setCurrentOrganization(organization);
      localStorage.setItem(CURRENT_ORGANIZATION_KEY, id);
    },
    [organizationQuery.data, setCurrentOrganization]
  );

  useEffect(() => {
    if (organizationQuery.data?.length > 0) {
      const searchParams = new URLSearchParams(window.location.search);
      const lastSelectedOrganization =
        searchParams.get('organization_id') || localStorage.getItem(CURRENT_ORGANIZATION_KEY);

      const defaultOrganizationId = organizationQuery.data[0].id;

      const isOrganizationPresent = Boolean(
        organizationQuery?.data?.find((org) => org.id === lastSelectedOrganization)
      );

      const selectedOrganizationId = isOrganizationPresent
        ? lastSelectedOrganization
        : defaultOrganizationId;

      selectOrganization(selectedOrganizationId);
    }
  }, [organizationQuery.data]);

  const createOrganization = useCallback(
    async (params) => {
      const prevState = await queryClient.getQueryData(queryKey);
      try {
        queryClient.setQueryData(queryKey, (old) => [...old, params]);
        await createOrganizationMutation.mutateAsync(params);
        addNotification({
          type: 'info',
          message: `${params.name} organization has been created`
        });
        organizationQuery.refetch();
      } catch (error) {
        addNotification({
          type: 'error',
          message: `Failed to create ${params.name} organization`
        });
        queryClient.setQueryData(queryKey, () => prevState);
      }
    },
    [organizationQuery.data]
  );

  const updateOrganization = useCallback(
    async (organizationId, data) => {
      const prevState = await queryClient.getQueryData(queryKey);

      try {
        queryClient.setQueryData(
          queryKey,
          prevState.map((organization) => {
            if (organization.id === organizationId) {
              return { ...organization, ...data };
            }
            return organization;
          })
        );

        await updateOrganizationMutation.mutateAsync({
          id: organizationId,
          data
        });
      } catch (error) {
        queryClient.setQueryData(queryKey, () => prevState);
      }
    },
    [organizationQuery.data]
  );

  const deleteOrganization = useCallback(
    async (organizationId) => {
      const prevState = await queryClient.getQueryData(queryKey);
      try {
        queryClient.setQueryData(
          queryKey,
          prevState.filter((organization) => organization.id !== organizationId)
        );
        await deleteOrganizationMutation.mutateAsync(organizationId);
        organizationQuery.refetch();
      } catch (error) {
        queryClient.setQueryData(queryKey, () => prevState);
      }
    },
    [organizationQuery.data]
  );

  const value = useMemo(
    () => ({
      organizations: organizationQuery.data,
      currentOrganization,
      selectOrganization,
      createOrganization,
      updateOrganization,
      deleteOrganization
    }),
    [organizationQuery.data, currentOrganization, selectOrganization]
  );

  if (organizationQuery.isInitialLoading) return false;

  return <OrganizationsContext.Provider value={value}>{children}</OrganizationsContext.Provider>;
};

OrganizationsProvider.propTypes = {
  children: PropTypes.node
};

const useOrganizations = () => useContext(OrganizationsContext);

export { OrganizationsContext, OrganizationsProvider, useOrganizations };
